/**
 * PushBanner - slim bar prompting Joseph to enable Web Push on this device.
 * Shown only while pushState is 'default' or 'granted-unsubscribed' and the
 * banner has not been dismissed. Dismissal persists to localStorage so it
 * never nags again on this device (unless storage is cleared).
 */

import { signal, computed } from '@preact/signals';
import { pushState, enablePush } from '../core/push/push';
import styles from './PushBanner.module.css';

const DISMISS_KEY = 'push.bannerDismissed';

const dismissed = signal(localStorage.getItem(DISMISS_KEY) === '1');

function dismiss() {
  localStorage.setItem(DISMISS_KEY, '1');
  dismissed.value = true;
}

/** BannerSlot coordination (P1a): mirrors this banner's own visibility rule. */
export const pushBannerWantsToShow = computed(
  () =>
    !dismissed.value &&
    (pushState.value === 'default' || pushState.value === 'granted-unsubscribed'),
);

export function PushBanner() {
  if (dismissed.value) return null;

  const state = pushState.value;
  if (state !== 'default' && state !== 'granted-unsubscribed') return null;

  return (
    <div class={styles.banner} role="status">
      <span class={styles.text}>
        Enable notifications to get Jarvis alerts on this device
      </span>
      <button
        class={styles.enable}
        onClick={() => {
          enablePush();
        }}
        aria-label="Enable notifications"
      >
        Enable
      </button>
      <button
        class={styles.dismiss}
        onClick={dismiss}
        aria-label="Dismiss notification prompt"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  );
}
